import { type AbstractMesh, ImportMeshAsync, type Scene, TransformNode } from "@babylonjs/core";
import type { Entity } from "../gen/livesoup/v1/realtime_pb";
import { fileURL } from "../net/api";
import { requestedColliderMode, resolveColliderMode, type Vec3 } from "./colliderRules";
import { applyCollider, entityTriangles } from "./colliders";
import { buildPrimitive, parsePrimitive } from "./primitives";
import { TrapGuard } from "./trapGuard";
import { attachImported } from "./entityPlace";

interface Loaded { root: TransformNode; entity: Entity }

const NO_DRAFTS: ReadonlySet<string> = new Set();

/** Every EntityAdded replaces the store object, so identity is the change signal (e.g. promote turns a draft live). */
export function needsReload(loaded: Entity | undefined, next: Entity): boolean {
  return loaded !== next;
}

export class EntityManager {
  private loaded = new Map<string, Loaded>();
  private loading = new Map<string, Entity>();
  private current: Record<string, Entity> = {};

  private traps = new TrapGuard();
  /** Live rows that were this plot's draft a moment ago (a promote in flight): shown, not hidden, until the plot leaves `drafted`. */
  private flipped = new Set<string>();

  constructor(
    private readonly scene: Scene,
    private readonly onMeshes?: (meshes: AbstractMesh[], added: boolean) => void,
    /** Local player's feet, for the trap guard; absent in headless use (then nothing is ever suspended). */
    private readonly feet?: () => Vec3,
  ) {
    if (feet) scene.onBeforeRenderObservable.add(() => this.traps.tick(feet()));
  }

  /** After a spawn or a server correction: suspend colliders the player now stands inside (spec 2k §3.2). */
  recheckTraps(): void { if (this.feet) this.traps.recheck(this.feet()); }

  /**
   * Reconcile the scene with the store: load new or changed entities, dispose removed ones. A changed entity
   * keeps its old instance (and its collision) until the replacement has loaded, so a promote or a put_layout
   * never leaves a hole under a player standing on a GLB while it re-downloads.
   *
   * `drafted` names the plots that have a draft. A draft previews the next live layout, so those plots' live
   * entities stay out of the scene (only the owner and builders are sent drafts, so only they see this) and
   * come back by themselves once the plot leaves `drafted`. A promote flips the draft rows to live under the
   * same ids, one message at a time: a live row whose instance here is still the draft is not hidden, it
   * reloads in place, so nothing blinks out while the rest of the rows are on the wire.
   */
  sync(entities: Record<string, Entity>, drafted: ReadonlySet<string> = NO_DRAFTS): void {
    for (const id of this.flipped) if (!entities[id] || !drafted.has(entities[id].plotId)) this.flipped.delete(id);
    this.current = {};
    for (const [id, e] of Object.entries(entities)) if (!this.hiddenUnderDraft(e, drafted)) this.current[id] = e;
    for (const id of [...this.loaded.keys()]) if (!this.current[id]) this.remove(id);
    for (const [id, e] of Object.entries(this.current)) {
      if (needsReload(this.loaded.get(id)?.entity, e) && this.loading.get(id) !== e) void this.load(e);
    }
  }

  private hiddenUnderDraft(e: Entity, drafted: ReadonlySet<string>): boolean {
    if (e.stage !== "live" || !drafted.has(e.plotId)) return false;
    if (this.flipped.has(e.id)) return false;
    const was = this.loaded.get(e.id)?.entity ?? this.loading.get(e.id);
    if (was?.stage !== "draft") return true;
    this.flipped.add(e.id);
    return false;
  }

  private async load(e: Entity): Promise<void> {
    this.loading.set(e.id, e);
    const root = new TransformNode(`entity:${e.id}`, this.scene);
    try {
      const t = e.transform!;
      root.position.set(t.pos!.x, t.pos!.y, t.pos!.z);
      root.rotation.y = (t.yaw * Math.PI) / 180;
      root.scaling.setAll(t.scale || 1);
      let meshes: AbstractMesh[] = [];
      let isLabel = false;
      if (e.assetCode) {
        const result = await ImportMeshAsync(fileURL(e.assetCode), this.scene, { pluginExtension: ".glb" });
        meshes = result.meshes;
        attachImported(meshes, root);
      } else {
        const spec = parsePrimitive(e.componentsJson);
        if (spec) {
          isLabel = spec.shape === "label";
          const mesh = buildPrimitive(this.scene, `entity:${e.id}:prim`, spec);
          mesh.parent = root;
          meshes = [mesh];
        }
      }
      // Removed or replaced while the GLB was downloading: throw this copy away instead of leaving a ghost.
      if (this.current[e.id] !== e) {
        meshes.forEach((m) => m.dispose());
        root.dispose();
        return;
      }
      this.remove(e.id); // the instance this one replaces, if any: the new meshes already exist, so no gap
      for (const m of meshes) {
        m.metadata = { ...(m.metadata ?? {}), entityId: e.id };
        m.isPickable = true;
      }
      const mode = resolveColliderMode(requestedColliderMode(e.componentsJson), isLabel, entityTriangles(meshes));
      const col = applyCollider(this.scene, root, meshes, mode, e.id);
      if (col.bounds) this.traps.add(e.id, col.bounds, (on) => col.collidable.forEach((m) => { m.checkCollisions = on; }), this.feet?.() ?? null, col.overlaps);
      this.loaded.set(e.id, { root, entity: e });
      this.onMeshes?.(visibleChildren(root), true);
    } catch (err) {
      root.dispose();
      this.remove(e.id); // a stale instance must not outlive the store object it was built from
      console.warn("entity load failed", e.id, err);
    } finally {
      if (this.loading.get(e.id) === e) this.loading.delete(e.id);
    }
  }

  private remove(id: string): void {
    const l = this.loaded.get(id);
    if (!l) return;
    this.traps.remove(id);
    this.onMeshes?.(visibleChildren(l.root), false);
    l.root.getChildMeshes().forEach((m) => m.dispose());
    l.root.dispose();
    this.loaded.delete(id);
  }

  entityAt(mesh: AbstractMesh | null | undefined): Entity | null {
    const id = mesh?.metadata?.entityId as string | undefined;
    return id ? this.loaded.get(id)?.entity ?? null : null;
  }

  dispose(): void { for (const id of [...this.loaded.keys()]) this.remove(id); }
}

/** The shadow hook gets the rendered meshes only: the invisible collider box casts nothing. */
function visibleChildren(root: TransformNode): AbstractMesh[] {
  return root.getChildMeshes().filter((m) => m.isVisible);
}
